import type { Locale } from '@/i18n/i18n';
import type { ProofAnswerRowLabel } from './build-proof-answer';

/**
 * Chrome around the generated proof answer, per locale. The figures (rates, statute references,
 * signatures) come from `proof-answer.generated.ts`; these strings only describe them. Keep
 * them free of dates and countdowns: anything time-bound is computed at render time.
 */
export interface ProofAnswerCopy {
  kicker: string;
  heading: string;
  subheading: string;
  badge: string;
  question: string;
  answerLead: (rate: string) => string;
  answerBody: string;
  columns: { pkwiu: string; service: string; rate: string; basis: string };
  rows: Record<ProofAnswerRowLabel, string>;
  warningTitle: (refusedRate: string) => string;
  warningBody: string;
  rulingLink: (signature: string) => string;
  rulingIssued: (date: string) => string;
  source: string;
  startHeading: string;
  startBody: string;
  startCta: string;
}

const polishRate = (rate: string) => `${rate.replace('.', ',')}%`;

export const PROOF_ANSWER_COPY: Record<Locale, ProofAnswerCopy> = {
  pl: {
    kicker: 'Dowody, nie obietnice',
    heading: 'Tak odpowiada Bookeper',
    subheading:
      'Jedno pytanie, odpowiedź z działającego systemu: stawki z silnika podatkowego, podstawa prawna i interpretacje KIS, które ją potwierdzają.',
    badge: 'prawdziwa odpowiedź z systemu',
    question: 'Jaka stawka ryczałtu dla programisty, PKD 62.01?',
    answerLead: (rate) =>
      `Najczęściej ${polishRate(rate)} — ale o stawce decyduje PKWiU usługi, nie sam kod PKD 62.01.Z.`,
    answerBody:
      'Programowanie na zlecenie to PKWiU 62.01.1. Wsparcie techniczne i pozostałe usługi IT mają niższą stawkę, ale tylko wtedy, gdy rzeczywiście je świadczysz.',
    columns: { pkwiu: 'PKWiU', service: 'Usługa', rate: 'Stawka', basis: 'Podstawa' },
    rows: {
      programming: 'Programowanie, projektowanie i testy oprogramowania',
      softwareOriginals: 'Oryginały oprogramowania',
      softwareConsulting: 'Doradztwo w zakresie oprogramowania',
      itSupport: 'Pomoc techniczna IT',
      otherIt: 'Pozostałe usługi IT (bez instalowania oprogramowania)',
      softwareInstallation: 'Instalowanie oprogramowania',
    },
    warningTitle: (rate) => `Uwaga na ${polishRate(rate)} dla programowania`,
    warningBody:
      'Wnioskodawcy chcieli niższej stawki dla usług PKWiU 62.01.11. Dyrektor KIS uznał to stanowisko za nieprawidłowe:',
    rulingLink: (signature) => `Interpretacja ${signature}`,
    rulingIssued: (date) => `wydana ${date}`,
    source:
      'Źródło: tabela stawek silnika Bookeper (art. 12 ust. 1 uzpd) i korpus interpretacji indywidualnych KIS. Interpretacja wiąże tylko wnioskodawcę.',
    startHeading: 'Policz to dla swojej firmy',
    startBody:
      'Bookeper dobiera stawkę do Twoich faktur, liczy ryczałt i ZUS co miesiąc i pokazuje podstawę prawną każdej liczby.',
    startCta: 'Sprawdź swój plan w 1 minutę',
  },
  en: {
    kicker: 'Proof, not promises',
    heading: 'This is how Bookeper answers',
    subheading:
      'One question, answered by the running system: rates from the tax engine, the legal basis, and the KIS rulings that back it.',
    badge: 'a real answer from the system',
    question: 'Which flat-rate (ryczałt) tax rate applies to a programmer, PKD 62.01?',
    answerLead: (rate) =>
      `Usually ${rate}% — but the PKWiU code of the service decides, not the PKD 62.01.Z code alone.`,
    answerBody:
      'Programming to order is PKWiU 62.01.1. Technical support and other IT services carry a lower rate, but only if that is what you actually provide.',
    columns: { pkwiu: 'PKWiU', service: 'Service', rate: 'Rate', basis: 'Legal basis' },
    rows: {
      programming: 'Programming, software design and testing',
      softwareOriginals: 'Software originals',
      softwareConsulting: 'Software consulting',
      itSupport: 'IT technical support',
      otherIt: 'Other IT services (excluding software installation)',
      softwareInstallation: 'Software installation',
    },
    warningTitle: (rate) => `Careful with ${rate}% for programming`,
    warningBody:
      'Applicants asked for the lower rate for PKWiU 62.01.11 services. The Director of KIS held that position incorrect:',
    rulingLink: (signature) => `Ruling ${signature}`,
    rulingIssued: (date) => `issued ${date}`,
    source:
      "Source: the Bookeper tax engine's rate table (art. 12(1) of the Polish flat-rate tax act) and the corpus of KIS individual rulings. A ruling binds only its applicant.",
    startHeading: 'Work it out for your business',
    startBody:
      'Bookeper matches the rate to your invoices, computes the tax and ZUS every month and shows the legal basis of every figure.',
    startCta: 'See your plan in 1 minute',
  },
  uk: {
    kicker: 'Докази, а не обіцянки',
    heading: 'Так відповідає Bookeper',
    subheading:
      'Одне питання, відповідь працюючої системи: ставки з податкового рушія, правова підстава та інтерпретації KIS, які її підтверджують.',
    badge: 'справжня відповідь системи',
    question: 'Яка ставка ryczałt для програміста, PKD 62.01?',
    answerLead: (rate) =>
      `Найчастіше ${polishRate(rate)} — але ставку визначає PKWiU послуги, а не сам код PKD 62.01.Z.`,
    answerBody:
      'Програмування на замовлення — це PKWiU 62.01.1. Технічна підтримка та інші ІТ-послуги мають нижчу ставку, але лише якщо Ви справді їх надаєте.',
    columns: { pkwiu: 'PKWiU', service: 'Послуга', rate: 'Ставка', basis: 'Підстава' },
    rows: {
      programming: 'Програмування, проєктування й тестування ПЗ',
      softwareOriginals: 'Оригінали програмного забезпечення',
      softwareConsulting: 'Консультування щодо ПЗ',
      itSupport: 'Технічна ІТ-підтримка',
      otherIt: 'Інші ІТ-послуги (крім встановлення програмного забезпечення)',
      softwareInstallation: 'Встановлення програмного забезпечення',
    },
    warningTitle: (rate) => `Обережно з ${polishRate(rate)} для програмування`,
    warningBody:
      'Заявники просили нижчу ставку для послуг PKWiU 62.01.11. Директор KIS визнав цю позицію неправильною:',
    rulingLink: (signature) => `Інтерпретація ${signature}`,
    rulingIssued: (date) => `видана ${date}`,
    source:
      'Джерело: таблиця ставок податкового рушія Bookeper (ст. 12 п. 1 закону про ryczałt) і корпус індивідуальних інтерпретацій KIS. Інтерпретація зобовʼязує лише заявника.',
    startHeading: 'Порахуйте це для своєї фірми',
    startBody:
      'Bookeper підбирає ставку до Ваших фактур, щомісяця рахує податок і ZUS та показує правову підставу кожної цифри.',
    startCta: 'Перевірте свій план за 1 хвилину',
  },
};
